import { withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { formatGraphqlProductOnwership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import GetProductOwnership from 'bundles/enroll/utils/queries/GetProductOwnershipQuery.graphql';
import type {
  GetProductOwnershipQuery,
  GetProductOwnershipQueryVariables,
} from 'bundles/enroll/utils/queries/__generated__/GetProductOwnershipQuery';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments/common/ProductType';

export type ProductOwnershipsInputProps = {
  productOwnershipsId?: string;
};
export type OptionProps = {
  isNotRequired?: boolean;
};

type InputProps = ProductOwnershipsInputProps;

export type PropsFromWithProductOwnerships = {
  productOwnerships?: FormattedGraphqlProductOwnership;
};

/** @deprecated Use the useProductOwnerships custom hook instead */
export const withProductOwnerships = <WithProductOwnershipsProps extends InputProps>({
  isNotRequired = false,
}: OptionProps = {}) =>
  waitForGraphQL<
    WithProductOwnershipsProps,
    GetProductOwnershipQuery,
    GetProductOwnershipQueryVariables,
    PropsFromWithProductOwnerships
  >(GetProductOwnership, {
    skip: (props) => !user.isAuthenticatedUser() || !props.productOwnershipsId,
    options: ({ productOwnershipsId }) => {
      if (!productOwnershipsId) {
        throw new Error(`expected valid productOwnershipsId`);
      }
      return {
        variables: {
          productOwnershipId: productOwnershipsId,
        },
        context: { clientName: 'gatewayGql' },
      };
    },
    props: ({ data, ownProps }) => {
      return {
        ...ownProps,
        productOwnerships: formatGraphqlProductOnwership(data?.Entitlement?.queryProductOwnership),
      };
    },
  });

type SpecializationIdProps = {
  s12nId: string;
};

export const withSpecializationProductOwnershipsId = withProps<ProductOwnershipsInputProps, SpecializationIdProps>(
  ({ s12nId }) => {
    const productOwnershipsId = user.isAuthenticatedUser()
      ? tupleToStringKey([user.get().id, SPECIALIZATION, s12nId])
      : undefined;
    return { productOwnershipsId };
  }
);

type VerifiedCertificateIdProps = {
  courseId?: string;
};
export const withVerifiedCertificateProductOwnershipsId = withProps<
  ProductOwnershipsInputProps,
  VerifiedCertificateIdProps
>(({ courseId }) => {
  const productOwnershipsId =
    user.isAuthenticatedUser() && courseId
      ? tupleToStringKey([user.get().id, VERIFIED_CERTIFICATE, courseId])
      : undefined;
  return { productOwnershipsId };
});
